import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ApiError, unwrap } from '@/api/envelope';
import { updateOrganization } from '@/api/generated';
import type { OrganizationResponse } from '@/api/generated';
import { applyServerErrors } from '@/api/serverErrors';
import { ContactFields } from '@/components/form/ContactFields';
import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { useUnsavedChanges } from '@/components/form/useUnsavedChanges';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { useToast } from '@/components/ui/useToast';
import { BILLING_CYCLES, INVOICE_STATUSES, PAYMENT_STATUSES } from '@/lib/organization';

import {
  CONTRACT_DATES_MESSAGE,
  billingFormSchema,
  isContractDateRefusal,
  toBillingFormValues,
  toBillingUpdatePayload,
  type BillingDirtyFields,
  type BillingFormOutput,
  type BillingFormValues,
} from './billingSchema';
import { storeSavedOrganization } from './organizationCache';

/**
 * The server's field names to the form's, in page order. The billing contact
 * is nested, and the server names a refused field by the last part of its
 * path alone - so its "email" and the rest arrive bare.
 */
const SERVER_FIELDS = {
  plan_name: 'plan_name',
  plan_cost: 'plan_cost',
  plan_currency: 'plan_currency',
  plan_billing_cycle: 'plan_billing_cycle',
  contract_start_date: 'contract_start_date',
  contract_end_date: 'contract_end_date',
  renewal_date: 'renewal_date',
  invoice_status: 'invoice_status',
  payment_status: 'payment_status',
  last_invoice_number: 'last_invoice_number',
  billing_notes: 'billing_notes',
  name: 'billing_contact_name',
  title: 'billing_contact_title',
  email: 'billing_contact_email',
  phone: 'billing_contact_phone',
} as const;

interface BillingFormProps {
  organizationId: string;
  record: OrganizationResponse;
  /**
   * True when this sits inside a CollapsibleCard, which supplies the card and
   * the heading. Without it the form draws its own, which is how the tests
   * and any other caller still get a whole card.
   */
  embedded?: boolean;
}

/**
 * What the account costs, what was agreed, and where the invoicing stands.
 *
 * Its own form and its own Save, separate from the details form above it. The
 * two save different slices of one record through the same endpoint, and
 * keeping them apart means a slip in billing is never saved alongside an
 * unrelated edit to the organization's profile.
 *
 * Billing is recorded here, not processed: the portal takes no payment.
 */
export function BillingForm({ organizationId, record, embedded }: BillingFormProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [formError, setFormError] = useState<string | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    setFocus,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<BillingFormValues, unknown, BillingFormOutput>({
    resolver: zodResolver(billingFormSchema),
    defaultValues: toBillingFormValues(record),
  });

  // Inside this card, not above the details form: a billing failure shown up
  // there reads as a complaint about fields nobody touched.
  useEffect(() => {
    if (!formError) return;
    alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    alertRef.current?.focus();
  }, [formError]);

  // The same flag the Save button reads, so the prompt and the button cannot
  // disagree about whether anything is unsaved.
  const unsaved = useUnsavedChanges(isDirty);

  const save = useMutation({
    mutationFn: async (values: BillingFormOutput) => {
      const reply = await updateOrganization({
        path: { organization_id: organizationId },
        body: toBillingUpdatePayload(
          values,
          dirtyFields as BillingDirtyFields,
          record.billing_contact,
        ),
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => setFormError(null),
    onSuccess: async (saved) => {
      /*
       * Reset to what the backend stored, not to what was typed. It rounds a
       * cost to the penny, so 1500.5 comes back as 1500.50 and the box changes
       * a moment after Save. That is the stored value, and showing anything
       * else would be showing a number the account does not hold.
       */
      reset(toBillingFormValues(saved));
      storeSavedOrganization(queryClient, saved);
      toast.show({ message: 'Billing details saved.' });
      await queryClient.invalidateQueries({ queryKey: ['staff', 'organizations'] });
    },
    onError: (error) => {
      const message = error instanceof ApiError ? (error.serverMessage ?? '') : '';

      /*
       * The backend refuses an end before a start in two places, with two
       * wordings - one of them "Body: Value error, contract_end_date cannot be
       * earlier than contract_start_date". Both name the field, which is all
       * that is wanted from them: the sentence staff read is the portal's, the
       * same one the form itself uses when both dates are on screen.
       */
      if (isContractDateRefusal(message)) {
        setError('contract_end_date', { message: CONTRACT_DATES_MESSAGE });
        return;
      }

      // Each field's message lands on its field; whatever concerns no field
      // goes to the alert inside this card.
      const { remainder } = applyServerErrors(
        error,
        { setError, setFocus },
        SERVER_FIELDS,
      );
      if (remainder) setFormError(remainder);
    },
  });

  return (
    <Paper
      variant={embedded ? 'elevation' : 'outlined'}
      elevation={0}
      component="form"
      onSubmit={(event) => void handleSubmit((values) => save.mutate(values))(event)}
      sx={{
        p: embedded ? 0 : 3,
        maxWidth: 760,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        bgcolor: 'transparent',
      }}
      noValidate
      data-testid="billing-form"
    >
      {embedded ? null : (
        <Box>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
            Billing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Recorded here, invoiced outside the platform. No payment is taken.
          </Typography>
        </Box>
      )}

      {formError ? (
        <Alert
          ref={alertRef}
          severity="error"
          role="alert"
          tabIndex={-1}
          data-testid="billing-form-alert"
          sx={{ '&:focus': { outline: 'none' } }}
        >
          {formError}
        </Alert>
      ) : null}

      <Field
        label="Plan name"
        error={errors.plan_name?.message}
        testId="billing-form-plan-name"
      >
        <TextField placeholder="Standard" {...register('plan_name')} />
      </Field>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '2 1 200px' }}>
          <Field
            label="Plan cost"
            hint="Per billing cycle, to the penny."
            error={errors.plan_cost?.message}
            testId="billing-form-plan-cost"
          >
            <TextField
              placeholder="1500.00"
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              {...register('plan_cost')}
            />
          </Field>
        </Box>
        <Box sx={{ flex: '1 1 120px' }}>
          <Field
            label="Currency"
            error={errors.plan_currency?.message}
            testId="billing-form-currency"
          >
            <TextField placeholder="USD" {...register('plan_currency')} />
          </Field>
        </Box>
      </Box>

      {/* Controlled, so a reset after a save reaches it. A select given only a
          defaultValue reads that value once and never looks again. */}
      <Controller
        name="plan_billing_cycle"
        control={control}
        render={({ field }) => (
          <Field
            label="Billing cycle"
            error={errors.plan_billing_cycle?.message}
            testId="billing-form-billing-cycle"
          >
            <TextField select {...field} value={field.value ?? ''}>
              <MenuItem value="">Not set</MenuItem>
              {BILLING_CYCLES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Field>
        )}
      />

      <Field
        label="Contract start"
        error={errors.contract_start_date?.message}
        testId="billing-form-contract-start"
      >
        <TextField
          type="date"
          slotProps={{ inputLabel: { shrink: true } }}
          {...register('contract_start_date')}
        />
      </Field>

      <Field
        label="Contract end"
        error={errors.contract_end_date?.message}
        testId="billing-form-contract-end"
      >
        <TextField
          type="date"
          slotProps={{ inputLabel: { shrink: true } }}
          {...register('contract_end_date')}
        />
      </Field>

      <Field
        label="Renewal date"
        error={errors.renewal_date?.message}
        testId="billing-form-renewal-date"
      >
        <TextField
          type="date"
          slotProps={{ inputLabel: { shrink: true } }}
          {...register('renewal_date')}
        />
      </Field>

      <Controller
        name="invoice_status"
        control={control}
        render={({ field }) => (
          <Field
            label="Invoice status"
            error={errors.invoice_status?.message}
            testId="billing-form-invoice-status"
          >
            <TextField select {...field} value={field.value ?? ''}>
              <MenuItem value="">Not set</MenuItem>
              {INVOICE_STATUSES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Field>
        )}
      />

      <Controller
        name="payment_status"
        control={control}
        render={({ field }) => (
          <Field
            label="Payment status"
            error={errors.payment_status?.message}
            testId="billing-form-payment-status"
          >
            <TextField select {...field} value={field.value ?? ''}>
              <MenuItem value="">Not set</MenuItem>
              {PAYMENT_STATUSES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Field>
        )}
      />

      <Field
        label="Last invoice number"
        error={errors.last_invoice_number?.message}
        testId="billing-form-last-invoice-number"
      >
        <TextField {...register('last_invoice_number')} />
      </Field>

      <Field
        label="Billing notes"
        hint="Internal. Not shown to the organization."
        error={errors.billing_notes?.message}
        testId="billing-form-notes"
      >
        <TextField multiline minRows={3} {...register('billing_notes')} />
      </Field>

      <ContactFields
        testId="billing-form-contact"
        labelPrefix="Billing"
        names={{
          name: 'billing_contact_name',
          title: 'billing_contact_title',
          email: 'billing_contact_email',
          phone: 'billing_contact_phone',
        }}
        register={register}
        errors={errors}
      />

      <Box>
        {/* Named for the form it saves, because this page has two Save
            buttons and "Save" alone would not say which. */}
        <Button
          type="submit"
          loading={save.isPending}
          disabled={!isDirty}
          data-testid="billing-form-save-button"
        >
          Save billing
        </Button>
      </Box>

      <UnsavedChangesDialog {...unsaved} />
    </Paper>
  );
}
